import { path, curry, pathOr, pipe, values, pluck, sum } from "ramda";
import { createSelector } from "reselect";

const grab = curry((spec, data) => {
  const output = {};
  for (const key in spec) {
    const fn = spec[key];
    if (typeof fn === "function") {
      output[key] = fn(data);
    } else {
      output[key] = grab(fn, data);
    }
  }
  return output;
});

const data = grab({
  sell: {
    cart: path(["cartSell"]),
    cards: path(["cards"])
  },
  buy: {
    cart: path(["cartBuy"]),
    cards: path(["cardsBuy"])
  }
});

const side = (state, props = {}) => {
  if (!/(buy|sell)/.test(side))
    throw new Error("Must provide an appropriate side");
  return props.side;
};

export const cartId = createSelector(data, side, (data, side) =>
  pathOr(null, [side, "cart", "cartId"], data)
);

export const count = createSelector(data, side, (data, side) =>
  pipe(pathOr({}, [side, "cards"]), values, pluck("quantity"), sum)(data)
);

const cardsBuy = ({ cardsBuy }) => cardsBuy;
const coupon = ({ coupon }) => coupon;
const paymentType = ({ paymentType }) => paymentType;

export const getCartTotals = createSelector(
  cardsBuy,
  coupon,
  paymentType,
  (cardArray, couponParam, paymentTypeParam) => {
    const totals = {
      subTotal: 0,
      totalSavings: 0,
      orderTotal: 0,
      extraTwoPercentSavings: 0,
      couponSavings: couponParam.discount,
      totalFaceValue: 0,
      thereIsPhysical: false,
      isStoreCreditGreaterThanTotal: false
    };
    Object.keys(cardArray).forEach(card => {
      const theCard = cardArray[card];
      totals.subTotal += Number(theCard.cost) * Number(theCard.quantity);
      totals.totalFaceValue +=
        Number(theCard.balance) * Number(theCard.quantity);
      totals.totalSavings +=
        (Number(theCard.balance) - Number(theCard.cost)) *
        Number(theCard.quantity);

      if (!totals.thereIsPhysical && theCard.type === "physical") {
        totals.thereIsPhysical = true;
      }
    });

    if (totals.subTotal) {
      if (paymentTypeParam === "CRYPTO" || paymentTypeParam === "ACH") {
        totals.extraTwoPercentSavings =
          (totals.subTotal - totals.couponSavings) * 0.02;
        totals.orderTotal =
          totals.subTotal -
          totals.couponSavings -
          totals.extraTwoPercentSavings;
      } else {
        totals.orderTotal = totals.subTotal - totals.couponSavings;
      }
    } else {
      totals.couponSavings = 0;
    }

    totals.totalSavings =
      totals.totalSavings +
      totals.couponSavings +
      totals.extraTwoPercentSavings;

    totals.isStoreCreditGreaterThanTotal =
      totals.totalSavings > 0 &&
      Object.keys(cardArray).length &&
      totals.orderTotal === 0;

    return totals;
  }
);

const paymentMethods = ({ paymentMethods }) => paymentMethods;
const selectedId = ({ selectedId }) => selectedId;
const filterCreditCards = ({ paymentMethods }) =>
  paymentMethods.filter(item => item.paymentMethod === "CREDITCARD");
const filterBankAccounts = ({ paymentMethods }) =>
  paymentMethods.filter(item => item.paymentMethod === "ACH");
const addedPaymentMethodId = ({ addedPaymentMethodId }) => addedPaymentMethodId;
const achAuthorized = ({ achAuthorized }) => achAuthorized;
const defaultSellBankAccount = ({ defaultSellBankAccount }) =>
  defaultSellBankAccount;

export const getDefaultPaymentMethod = createSelector(
  paymentMethods,
  addedPaymentMethodId,
  (paymentMethodsParam, addedPaymentMethodParam) => {
    if (addedPaymentMethodParam) {
      return paymentMethodsParam.find(
        item => item.id === addedPaymentMethodParam
      );
    }
    let defaultMethod = paymentMethodsParam.find(item => item.default === true);
    return defaultMethod;
  }
);

export const getSelectedMethod = createSelector(
  paymentMethods,
  selectedId,
  (paymentMethodsParam, selectedIdParam) => {
    const selectedMethod = paymentMethodsParam.find(
      item => selectedIdParam === item.id
    );
    return selectedMethod;
  }
);

export const getCreditCards = createSelector(
  filterCreditCards,
  filterCreditCardsParam => filterCreditCardsParam
);

export const getBuyAndSellBankAccounts = createSelector(
  filterBankAccounts,
  filterBankAccountsParam => {
    return filterBankAccountsParam;
  }
);

export const getSellBankAccountsWithAdded = createSelector(
  filterBankAccounts,
  defaultSellBankAccount,
  (filterBankAccountsParam, defaultSellBankAccountParam) => {
    let allBankAccounts = filterBankAccountsParam;

    if (
      defaultSellBankAccountParam &&
      Object.keys(defaultSellBankAccountParam).length > 0
    ) {
      allBankAccounts = [...filterBankAccountsParam].sort(item => {
        if (
          item.routing.substring(item.routing.length - 4) ===
            defaultSellBankAccountParam.routingNumber.substring(
              defaultSellBankAccountParam.routingNumber.length - 4
            ) &&
          item.account.substring(item.account.length - 4) ===
            defaultSellBankAccountParam.accountNumber.substring(
              defaultSellBankAccountParam.accountNumber.length - 4
            ) &&
          item.account_type === defaultSellBankAccountParam.accountType
        ) {
          return -1;
        } else {
          return 0;
        }
      });
      return {
        allBankAccounts,
        defaultSelectedId: allBankAccounts[0]
          ? allBankAccounts[0].paymentDetails.id
          : ""
      };
    } else {
      return {
        allBankAccounts,
        defaultSelectedId: allBankAccounts[0]
          ? allBankAccounts[0].paymentDetails.id
          : ""
      };
    }
  }
);

export const getDefaultCreditCard = createSelector(
  filterCreditCards,
  filterCreditCardsParam => {
    const defaultCreditCard =
      filterCreditCardsParam.find(item => item.default) ||
      filterCreditCardsParam[0];
    return defaultCreditCard;
  }
);

export const getRealDefaultPaymentMethod = createSelector(
  paymentMethods,
  paymentMethodsParam => {
    return paymentMethodsParam.find(item => item.default === true);
  }
);

export const getRefactoredPaymentMethods = createSelector(
  paymentMethods,
  addedPaymentMethodId,
  achAuthorized,
  (paymentMethodsParam, addedPaymentMethodParam, achAuthorizedParam) => {
    let paymentMethodArray;
    if (!achAuthorizedParam) {
      paymentMethodArray = paymentMethodsParam.filter(
        item => !item.paymentMethod.toLowerCase().startsWith("ach")
      );
    } else {
      paymentMethodArray = paymentMethodsParam;
    }
    if (addedPaymentMethodParam) {
      const addedMethod = paymentMethodsParam.find(
        item => item.id === addedPaymentMethodParam
      );
      if (addedMethod) {
        paymentMethodArray = paymentMethodsParam.filter(
          item => item.id !== addedPaymentMethodParam
        );
        paymentMethodArray.unshift(addedMethod);
      }
    }
    return paymentMethodArray;
  }
);
